import React, { useState } from "react";
import Button from "../../components/ui/Button";
import goal from "../../assets/images/Roulette-blur.png";
import ticket from "../../assets/icons/ticket.svg"; // Regular import
import TransactionTicketPopup from "../../components/Popup/TransactionTicketPopup ";
// Import default logos to use as fallbacks
import GameImage2 from "../../assets/images/games-1.webp";

export default function ViewDetailsCard({
  game,
  role,
  logo,
  numbers,
  drawDate,
  drawTime,
  totalHits,
  totalWin,
  ticketNumber,
  amount,
  isFloatingWin,
  floatingWinSource,
  floatingWinSourceRole,
}) {
  const [ticketPopup, setTicketPopup] = useState(false);
  const [ticketLoading, setTicketLoading] = useState(false);

  // Ensure numbers is an array
  const safeNumbers = Array.isArray(numbers) ? numbers : [];

  // Add default colors to each number if not provided
  const colorMap = {
    0: "#0ED173", // Green
    1: "#0E64D1", // Blue
    2: "#0E64D1", // Blue
    3: "#0E64D1", // Blue
    4: "#0E64D1", // Blue
    5: "#D92000", // Red
    6: "#0E64D1", // Blue
    7: "#D92000", // Red
    8: "#0E64D1", // Blue
    9: "#0ED173", // Green
  };

  // Process numbers to have the right styling
  const processedNumbers = safeNumbers.map((digit) => {
    const num = digit.num || 0;
    return {
      num,
      bgColor: colorMap[num] || "#0E64D1", // Default to blue if not found
    };
  });

  const formatTo12Hours = (time) => {
    const [hours, minutes] = time.split(":");
    const formattedHours = parseInt(hours, 10) % 12 || 12;
    const amPm = parseInt(hours, 10) < 12 ? "AM" : "PM";
    return `${formattedHours}:${minutes} ${amPm}`;
  };

  // Determine which logo to use
  const LogoComponent = logo || GameImage2;

  return (
    <div className="bg-white border border-[#E1E7EF] mb-2.5 rounded-md overflow-hidden relative">
      <div className="p-2.5">
        {/* Header section with logo, name and button */}
        <div className="flex items-start justify-between mb-3 relative">
          <div className="flex items-start gap-2.5">
            {/* Game logo */}
            <div className="w-[70px] h-[70px] rounded-lg overflow-hidden flex-shrink-0">
              {typeof LogoComponent === "function" ? (
                <img src={LogoComponent} alt={game} width={70} height={70} />
              ) : (
                <div className="bg-gray-200 w-full h-full flex items-center justify-center">
                  <span className="text-gray-600 font-bold">
                    {(game || "").substring(0, 2)}
                  </span>
                </div>
              )}
            </div>

            <div>
              <h3 className="font-bold text-base text-[#12131A] leading-tight mb-1">
                {game}

                <span
                  className={`ml-2 inline-block text-[10px] text-white font-normal px-2 py-0.5 rounded-[20px] ${
                    role === "Agent"
                      ? "bg-[#FD6C23]"
                      : role === "Player"
                      ? "bg-[#7051EB]"
                      : "bg-[#EF3E3E]"
                  }`}
                >
                  {role}
                </span>
              </h3>

              {/* Bet numbers */}
              <div className="mb-2 mt-3">
                <div className="flex gap-1">
                  {processedNumbers.map((digit, i) => (
                    <div
                      key={i}
                      className="relative flex items-center justify-center w-6 h-6"
                    >
                      {/* Background pattern */}
                      <div
                        className="absolute inset-0 rounded-full"
                        style={{
                          transform: "rotate(25deg)",
                          background: `conic-gradient(
                      from 0deg,
                      ${digit.bgColor} 0deg 40deg,
                      white 40deg 90deg,
                      ${digit.bgColor} 90deg 130deg,
                      white 130deg 180deg,
                      ${digit.bgColor} 180deg 220deg,
                      white 220deg 270deg,
                      ${digit.bgColor} 270deg 310deg,
                      white 310deg 360deg
                    )`,
                        }}
                      />

                      {/* Inner circle with number */}
                      <div
                        className="relative flex items-center justify-center w-[22px] h-[22px] rounded-full text-white text-sm font-medium"
                        style={{ backgroundColor: digit.bgColor }}
                      >
                        {digit.num}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <Button
            className="bg-[#0E64D1] text-white text-xs px-2 py-1.5 rounded-[4px] whitespace-nowrap min-w-[90px] flex-shrink-0"
            text="View Details"
            onClick={() => setTicketPopup(true)}
          />
        </div>

        {/* Draw date and time */}
        <div className="grid grid-cols-3 gap-4 mt-4 mb-4">
          <div></div>
          <div>
            <div className="text-[10px] text-[#65758B] mb-1">Draw Date</div>
            <div className="font-semibold text-xs text-[#12131A]">
              {drawDate}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-[#65758B] mb-1">Draw Time</div>
            <div className="font-semibold text-xs text-[#12131A]">
              {formatTo12Hours(drawTime)}
            </div>
          </div>
        </div>

        {isFloatingWin && (
          <div className="w-full mb-2 bg-[#EF3E3E] py-2 text-center shadow-md">
            <span className="text-[13px] text-white font-bold tracking-wide">
              Floating Win{floatingWinSource ? ` from ${floatingWinSource} (${floatingWinSourceRole})` : ''}
            </span>
          </div>
        )}

        {/* Bottom boxes */}
        <div className="flex gap-2 mt-4">
          <div
            className="rounded-lg flex justify-between overflow-hidden w-[30%]"
            style={{
              background: "linear-gradient(180deg, #65758B 0%, #12131A 100%)",
            }}
          >
            <div className="text-white p-2.5 self-center">
              <div className="text-xs font-normal">Amount</div>
              <div className="font-semibold text-normal">
                {typeof amount === "number" ? amount.toFixed(2) : amount}
              </div>
            </div>
          </div>

          <div
            className="rounded-lg flex justify-between overflow-hidden w-[35%]"
            style={{
              background: "linear-gradient(180deg, #0E64D1 0%, #132A67 100%)",
            }}
          >
            <div className="text-white p-2.5 self-center">
              <div className="text-xs font-normal">Type</div>
              <div className="font-semibold text-normal capitalize">
                {totalHits}
              </div>
            </div>
            <div className="self-start">
              <img
                src={goal}
                alt="Type"
                width={62}
                height={62}
                style={{ opacity: 0.3 }}
              />
            </div>
          </div>

          <div
            className="rounded-lg flex justify-between overflow-hidden w-[35%] relative"
            style={{
              background: "linear-gradient(180deg, #FEDA50 0%, #AB431A 100%)",
            }}
          >
            <div className="text-white p-2.5 self-center z-10">
              <div className="text-xs font-normal">Total Win</div>
              <div className="font-semibold text-normal">
                {typeof totalWin === "number"
                  ? totalWin.toLocaleString("en-US", {
                      style: "currency",
                      currency: "PHP",
                    })
                  : totalWin}
              </div>
            </div>
            <div
              style={{
                backgroundImage: `url('/src/assets/images/Trophy.png')`,
                backgroundSize: "contain",
                backgroundPosition: "right",
                backgroundRepeat: "no-repeat",
                width: "100%",
                height: "100%",
                zIndex: 1,
                position: "absolute",
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Ticket number */}
      <div className="flex items-center gap-2 px-2 pt-1 text-[#65758B] text-[10px] bg-[#E1E7EF] font-normal border-t border-dashed border-[#65758B]">
        <img
          src={ticket}
          alt=""
          width={14}
          height={14}
          className="rounded-lg"
        />
        Ticket #: {ticketNumber}
      </div>

      <TransactionTicketPopup
        isOpen={ticketPopup}
        ticketId={ticketNumber}
        onClose={() => setTicketPopup(false)}
        onLoading={setTicketLoading}
      />
    </div>
  );
}
